import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  ViewContainerRef,
  output,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, of, switchMap } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { AccountSelectors, CurrentUserSelectors, UserSelectors } from '@core/account-data';
import type { CurrentUser, User } from '@core/account-types';
import { FullCandidateSelectors } from '@core/candidate-data';
import type { FullCandidate } from '@core/candidate-types';
import {
  CandidateSharingAnyoneTabComponent,
  CandidateSharingDialogComponent,
} from '@core/candidate-ui';
import { ShareCandidatesWithContextAnalytics } from '@core/candidate-util';
import { filterBoolean, filterDefined } from '@core/common-legacy';
import type { Company } from '@core/company-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  CandidateShareActions,
  CandidateShareSelectors,
  SharingContainerActions,
  SharingContainerSelectors,
} from '@core/sharing-container-data';
import type {
  CandidateShareEntity,
  ShareCandidateDialogCloseData,
  SharingContainerApi,
} from '@core/sharing-container-types';
import { injectShareCandidateDialogOpener } from '@core/sharing-container-types';
import {
  BadgeComponent,
  DialogService,
  DialogWidth,
  DropdownComponent,
  DropdownWidth,
  HeadingComponent,
  RtTooltip,
  SpinnerComponent,
  TabComponent,
  TabsComponent,
} from '@core/user-interface-legacy';

import { CandidateSharingTeamMembersTabComponent } from '../candidate-sharing-team-members-tab/candidate-sharing-team-members-tab.component';

export enum DropdownTab {
  TeamMembers = 'TeamMembers',
  Anyone = 'Anyone',
}

@Component({
  selector: 'rt-candidate-sharing-dropdown',
  imports: [
    DropdownComponent,
    HeadingComponent,
    TabsComponent,
    TabComponent,
    CandidateSharingTeamMembersTabComponent,
    CandidateSharingAnyoneTabComponent,
    TranslatePipe,
    BadgeComponent,
    RtTooltip,
    CommonModule,
    SpinnerComponent,
  ],
  templateUrl: './candidate-sharing-dropdown.component.html',
  styleUrls: ['./candidate-sharing-dropdown.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ShareCandidatesWithContextAnalytics],
})
export class CandidateSharingDropdownComponent {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogSvc = inject(DialogService);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly shareCandidatesWithContextAnalytics = inject(
    ShareCandidatesWithContextAnalytics,
  );
  private readonly actions$ = inject(Actions);
  private readonly i18n = inject(I18nService);

  public readonly candidateId = input<number>(undefined!);
  @Input() public sharedAdminCount = 0;
  @Input() public sharedContainerCount = 0;

  public viewReady: boolean = false;
  public candidate: FullCandidate;
  public currentCompany: Company;
  public currentUser: CurrentUser;
  public candidateShare: CandidateShareEntity;
  public sharingDisabled = false;

  public DropdownWidth: typeof DropdownWidth = DropdownWidth;
  public DropdownTab: typeof DropdownTab = DropdownTab;
  public activeTab: DropdownTab = DropdownTab.TeamMembers;

  public newItemClick = output<void>();

  private shareCandidateDialogOpener = injectShareCandidateDialogOpener();

  public changeTab(tab: DropdownTab) {
    if (tab !== this.activeTab) {
      this.activeTab = tab;
      this.cdRef.detectChanges();
    }
  }

  public ngOnInit() {
    this.loadCandidateContainers(this.candidateId());
  }

  public ngAfterViewInit() {
    combineLatest([
      this.store
        .select(FullCandidateSelectors.byIdWithReferences(this.candidateId()))
        .pipe(filterBoolean),
      this.store.select(AccountSelectors.currentCompany).pipe(filterBoolean),
      this.store.select(CandidateShareSelectors.isPending).pipe(filterDefined),
      this.store.select(CandidateShareSelectors.withActivePublicLinks).pipe(filterBoolean),
      this.store.select(CurrentUserSelectors.data).pipe(filterBoolean),
      this.store.select(UserSelectors.entities()).pipe(filterDefined),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        ([candidate, currentCompany, isCandidateSharePending, candidateShare, currentUser, users]: [
          FullCandidate,
          Company,
          boolean,
          CandidateShareEntity,
          CurrentUser,
          User[],
        ]) => {
          this.candidate = candidate;
          this.currentCompany = currentCompany;
          this.candidateShare = candidateShare;
          this.sharedAdminCount = candidateShare.sharedAdminCount;
          this.sharedContainerCount = candidateShare.sharedContainerCount;
          this.sharingDisabled = !this.candidate.placements.length;
          this.currentUser = currentUser;
          this.viewReady = !isCandidateSharePending && !!candidateShare;
          this.cdRef.detectChanges();
        },
      );

    /* Leaving old flow with fetching candidate
     * but after change to /candidate/share
     * will use reference actions to determine
     * if candidate is in active shares
     */
    this.actions$
      .pipe(
        ofType(
          SharingContainerActions.saveSuccess,
          SharingContainerActions.removeOnePessimisticSuccess,
          SharingContainerActions.sendShareLinksSuccess,
        ),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadCandidateContainers(this.candidateId());
      });
  }

  public openContainerDialog(container?: Partial<SharingContainerApi>): void {
    let data$; // TODO: use selectors in dialog after flag remove
    if (container) {
      this.store.dispatch(SharingContainerActions.fetchOne({ payload: { id: container.id! } }));
      data$ = this.store
        .select(SharingContainerSelectors.byIdWithReferences(container.id!))
        .pipe(filterDefined, take(1));
    } else {
      data$ = of({});
    }

    data$
      .pipe(
        switchMap(data =>
          this.shareCandidateDialogOpener(
            <any>(container ? { container: data } : { candidates: [this.candidate] }),
          ),
        ),
        switchMap(dialog => dialog.afterClosed),
        filter(data => !!data?.visibleSections),
      )
      .subscribe((closeData: ShareCandidateDialogCloseData) => {
        this.shareCandidatesWithContextAnalytics.trackSavePublicLink({
          visibleSections: closeData.visibleSections,
          editableSections: closeData.editableSections,
          candidates: closeData.candidates,
        });
      });
  }

  public openContainerAssignmentsDialog(): void {
    this.dialogSvc.custom(
      CandidateSharingDialogComponent,
      { width: DialogWidth.SmallXX },
      {
        candidateId: this.candidate.id,
        dataScope: 'dropdown',
        heading: this.i18n.translate('app.candidate.share_assignments.heading_atc'),
        subheading: this.i18n.translate('app.candidate.share_assignments.subheading_atc'),
      },
      this.vcRef,
    );
  }

  private loadCandidateContainers(candidateId: number) {
    this.store.dispatch(CandidateShareActions.fetch({ payload: { candidateId } }));
  }
}
